import { EditorState, Modifier, SelectionState } from 'draft-js'

const HEADING_TYPES = ['heading', 'sub-heading']

/**
 * Enter at the end of a heading starts a body paragraph, as in most editors;
 * Draft.js would otherwise carry custom block types onto the new line.
 */
export const splitAfterHeading = (state: EditorState): EditorState | null => {
  const selection = state.getSelection()
  if (!selection.isCollapsed()) return null
  const content = state.getCurrentContent()
  const block = content.getBlockForKey(selection.getStartKey())
  if (
    !HEADING_TYPES.includes(block.getType()) ||
    selection.getStartOffset() !== block.getLength()
  ) {
    return null
  }
  const split = Modifier.splitBlock(content, selection)
  const asBody = Modifier.setBlockType(
    split,
    split.getSelectionAfter(),
    'unstyled'
  )
  return EditorState.push(state, asBody, 'split-block')
}

/**
 * The range of the link under the selection, if there is one: the selection
 * itself when text is selected inside a link, or the whole link when the
 * caret is just sitting in it -- so one button can both add and remove links.
 */
export const linkRangeAtSelection = (
  state: EditorState
): SelectionState | null => {
  const selection = state.getSelection()
  const content = state.getCurrentContent()
  const block = content.getBlockForKey(selection.getStartKey())
  const offset = selection.isCollapsed()
    ? Math.max(0, selection.getStartOffset() - 1)
    : selection.getStartOffset()
  const entityKey = block.getEntityAt(offset)
  if (!entityKey || content.getEntity(entityKey).getType() !== 'LINK') {
    return null
  }
  if (!selection.isCollapsed()) return selection

  let range: SelectionState | null = null
  block.findEntityRanges(
    character => character.getEntity() === entityKey,
    (start, end) => {
      if (start <= offset && offset < end) {
        range = SelectionState.createEmpty(block.getKey()).merge({
          anchorOffset: start,
          focusOffset: end
        }) as SelectionState
      }
    }
  )
  return range
}
